import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

/** Thin wrapper around `window.localStorage` with try/catch and SSR-safe access */
@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  private readonly storage: Storage | null;

  constructor(@Inject(DOCUMENT) doc: Document) {
    try {
      this.storage = doc.defaultView?.localStorage ?? null;
    } catch {
      this.storage = null;
    }
  }

  /** Whether read/write/remove can be attempted */
  readonly available = (): boolean => this.storage != null;

  getItem(key: string): string | null {
    if (!this.storage) {
      return null;
    }
    try {
      return this.storage.getItem(key);
    } catch {
      return null;
    }
  }

  /** Returns false if storage is unavailable, quota exceeded, or private mode blocks writes */
  setItem(key: string, value: string): boolean {
    if (!this.storage) {
      return false;
    }
    try {
      this.storage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }

  removeItem(key: string): boolean {
    if (!this.storage) {
      return false;
    }
    try {
      this.storage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  getJson<T>(key: string): T | null {
    const raw = this.getItem(key);
    if (raw == null) {
      return null;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  setJson(key: string, value: unknown): boolean {
    try {
      return this.setItem(key, JSON.stringify(value));
    } catch {
      return false;
    }
  }
}
